import { useRef } from 'react';
import { useSearchParams } from 'react-router';
import {
  PROFESSIONAL_ID_PARAM,
  SHIFT_IDS_PARAM,
  UTM_CAMPAIGN_PARAM,
  UTM_SOURCE_PARAM,
  TNotificationOrigin,
  TOfficeNotificationKey,
  parseNotificationParams,
} from '~/domain/OfficeNotification';
import { useTrackOnMount } from './useTrackOnMount';

export interface IOfficeNotificationLandingContext {
  origin: TNotificationOrigin;
  /** When the office landed in the portal — captured once on first render. */
  clickedAt: Date;
  /** Shift ids carried on the email link (`shift_ids` CSV). Empty when absent. */
  shiftIds: string[];
  /** Professional id carried on candidate-applied links, if any. */
  professionalId?: string;
}

export type TOfficeNotificationLandingHandler = (
  context: IOfficeNotificationLandingContext,
) => void;

export interface IUseOfficeNotificationLandingArgs {
  /**
   * Gate firing until the page has the data it needs (loaded job, shift list,
   * etc.). The event fires once, on the first render where this is true.
   */
  enabled?: boolean;
  /** Event to fire when the link carried no (or an unhandled) `utm_campaign`. */
  fallbackKey: TOfficeNotificationKey;
  /** One handler per notification key this page knows how to report. */
  handlers: Partial<Record<TOfficeNotificationKey, TOfficeNotificationLandingHandler>>;
}

/**
 * Fires a single `dental_portal:office_notification:*` event when an office
 * lands on a page from an email CTA (GOTU-4002, reading the GOTU-4135 UTM
 * contract).
 *
 * Thin feature glue over {@link useTrackOnMount}: this hook owns only the
 * office-notification specifics — reading the link params (`utm_source`,
 * `utm_campaign`, `shift_ids`, `professional_id`), choosing the matching handler
 * (falling back to `fallbackKey`), and stripping the params after firing so a
 * back-navigation can't re-fire it. The fire-once-when-ready lifecycle lives in
 * the reusable primitive. Pages stay a single declarative call describing only
 * *what* to report; shift/professional ids arrive from the link, not the route.
 */
export function useOfficeNotificationLanding({
  enabled = true,
  fallbackKey,
  handlers,
}: IUseOfficeNotificationLandingArgs): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const clickedAtRef = useRef(new Date());

  const { origin, notification, shiftIds, professionalId } =
    parseNotificationParams(searchParams);

  useTrackOnMount(
    () => {
      if (!origin) {
        return;
      }

      const key =
        notification && handlers[notification] ? notification : fallbackKey;
      const handler = handlers[key];
      if (!handler) {
        return;
      }

      handler({
        origin,
        clickedAt: clickedAtRef.current,
        shiftIds: shiftIds ?? [],
        professionalId,
      });

      // Strip the office-notification params so a back-navigation remount can't
      // re-fire. Campaign attribution (useCampaignTracker) has already captured
      // utm_* from the original URL by the time this navigation commits.
      const nextParams = new URLSearchParams(searchParams);
      [UTM_SOURCE_PARAM, UTM_CAMPAIGN_PARAM, SHIFT_IDS_PARAM, PROFESSIONAL_ID_PARAM].forEach(
        (param) => nextParams.delete(param),
      );
      setSearchParams(nextParams, { replace: true });
    },
    { enabled: enabled && Boolean(origin) },
  );
}
